import { logEvent } from '../analytics';
import { safeParseJson } from '../utils/safeParseJson';

const KEY = 'dg_offline_action_queue_v1';
const MAX = 50;

/** Fired when the queue length may have changed (enqueue, drain, clear). */
export const OFFLINE_QUEUE_CHANGED_EVENT = 'dg-offline-queue-changed' as const;

/** Serialize drains so reconnect + manual “process” don’t skip or race. */
let drainTail: Promise<void> = Promise.resolve();

function notifyQueueChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_CHANGED_EVENT));
}

export type OfflineQueuedAction = {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  at: number;
};

function read(): OfflineQueuedAction[] {
  try {
    const parsed = safeParseJson<unknown>(
      typeof window !== 'undefined' ? window.localStorage.getItem(KEY) : null,
      null
    );
    return Array.isArray(parsed) ? (parsed as OfflineQueuedAction[]) : [];
  } catch {
    return [];
  }
}

export function enqueueOfflineAction(
  entry: Omit<OfflineQueuedAction, 'id' | 'at'> & { at?: number }
): OfflineQueuedAction[] {
  if (typeof window === 'undefined') return read();
  const id = `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const at = entry.at ?? Date.now();
  const row: OfflineQueuedAction = {
    id,
    at,
    type: entry.type,
    payload: entry.payload || {},
  };
  const next = [row, ...read()].slice(0, MAX);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* quota */
  }
  try {
    logEvent('offline_action_enqueued', { action_type: entry.type });
  } catch {
    /* analytics must never break enqueue */
  }
  notifyQueueChanged();
  return next;
}

export function getOfflineActionQueue(): OfflineQueuedAction[] {
  return read()
    .filter((e) => e && typeof e.id === 'string' && typeof e.type === 'string')
    .sort((a, b) => a.at - b.at);
}

export function clearOfflineActionQueue(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  notifyQueueChanged();
}

/**
 * Process queued actions after reconnect (e.g. log, retry side effects).
 * Handler returns true to drop the item, false to keep for next drain.
 */
export async function drainOfflineActionQueue(
  handler: (action: OfflineQueuedAction) => Promise<boolean>
): Promise<void> {
  if (typeof window === 'undefined') return;
  drainTail = drainTail.then(() => runDrain(handler));
  return drainTail;
}

async function runDrain(handler: (action: OfflineQueuedAction) => Promise<boolean>): Promise<void> {
  const q = getOfflineActionQueue();
  if (q.length === 0) return;
  const kept: OfflineQueuedAction[] = [];
  for (const action of q) {
    try {
      const done = await handler(action);
      if (!done) kept.push(action);
    } catch {
      kept.push(action);
    }
  }
  try {
    window.localStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    /* ignore */
  }
  notifyQueueChanged();
}
